import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import inspectionsService from '../services/inspectionsService';
import styles from '../assets/inspectionsCreate.module.css';

const InspectionsCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [buildingManagers, setBuildingManagers] = useState([]);
  const [types, setTypes] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [errors, setErrors] = useState({});
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [showPropertyTypeahead, setShowPropertyTypeahead] = useState(false);
  const [propertySearchValue, setPropertySearchValue] = useState('');
  const propertyTypeaheadRef = useRef(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  // Click outside handler for property typeahead dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPropertyTypeahead && propertyTypeaheadRef.current && !propertyTypeaheadRef.current.contains(event.target)) {
        setShowPropertyTypeahead(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPropertyTypeahead]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [buildingManagersResponse, typesResponse] = await Promise.all([
        inspectionsService.getBuildingManagers(),
        inspectionsService.getInspectionTypes()
      ]);

      if (buildingManagersResponse && buildingManagersResponse.success) {
        setBuildingManagers(buildingManagersResponse.data || []);
      } else {
        toast.error('Failed to load building managers');
        setBuildingManagers([]);
      }

      if (typesResponse && typesResponse.success) {
        setTypes(typesResponse.data || []);
      } else {
        toast.error('Failed to load inspection types');
        setTypes([]);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Failed to load form data. Please check your connection.');
      setBuildingManagers([]);
      setTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePropertySearch = async (value) => {
    setPropertySearchValue(value);
    
    if (value.length > 0) {
      try {
        const response = await inspectionsService.searchProperties(value);
        // Laravel ajaxSearchAll returns array directly, but backend returns { properties: [...], success: true, data: [...] }
        // Handle both formats for compatibility
        let properties = [];
        if (Array.isArray(response)) {
          // Direct array format (Laravel's original format)
          properties = response;
        } else if (response && response.properties && Array.isArray(response.properties)) {
          // Laravel Bloodhound format: { properties: [...] }
          properties = response.properties;
        } else if (response && response.success && response.data) {
          // Our backend format: { success: true, data: [...] }
          properties = response.data;
        }
        
        if (properties.length > 0) {
          setFilteredProperties(properties);
          setShowPropertyTypeahead(true);
        } else {
          setFilteredProperties([]);
          setShowPropertyTypeahead(false);
        }
      } catch (error) {
        console.error('Error searching properties:', error);
        setFilteredProperties([]);
        setShowPropertyTypeahead(false);
      }
    } else {
      setShowPropertyTypeahead(false);
      setFilteredProperties([]);
    }
  };

  const selectProperty = (property) => {
    const newInspection = {
      property_id: property.id,
      property_name: property.name,
      day: '',
      time_from: '',
      time_to: '',
      description: ''
    };
    
    setInspections([...inspections, newInspection]);
    setPropertySearchValue('');
    setShowPropertyTypeahead(false);
  };

  const handleInspectionChange = (index, field, value) => {
    const newInspections = [...inspections];
    newInspections[index] = {
      ...newInspections[index],
      [field]: value
    };
    setInspections(newInspections);
    
    // Clear error when user starts typing
    if (errors[`inspections.${index}.${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`inspections.${index}.${field}`]: ''
      }));
    }
  };

  const removeInspection = (index) => {
    const newInspections = inspections.filter((_, i) => i !== index);
    setInspections(newInspections);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!inspections.length) {
      newErrors.inspections = 'You have to add at least one inspection (property)!';
    }

    inspections.forEach((inspection, index) => {
      if (!inspection.property_id) {
        newErrors[`inspections.${index}.property_id`] = 'This field is required';
      }
      if (!inspection.day) {
        newErrors[`inspections.${index}.day`] = 'This field is required';
      } else {
        // Validate date format (dd/mm/yyyy)
        const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
        if (!dateRegex.test(inspection.day)) {
          newErrors[`inspections.${index}.day`] = 'Not match the format dd/mm/yyyy';
        }
      }
      if (!inspection.time_from) {
        newErrors[`inspections.${index}.time_from`] = 'This field is required';
      } else {
        // Validate time format (HH:mm)
        const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
        if (!timeRegex.test(inspection.time_from)) {
          newErrors[`inspections.${index}.time_from`] = 'Not match the format HH:mm';
        }
      }
      if (inspection.time_to) {
        // Validate time format (HH:mm)
        const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
        if (!timeRegex.test(inspection.time_to)) {
          newErrors[`inspections.${index}.time_to`] = 'Not match the format HH:mm';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors below');
      return;
    }

    try {
      setSubmitting(true);
      
      const buildingManagerId = document.getElementById('building_manager_id')?.value;
      const typeId = document.getElementById('type_id')?.value;
      
      if (!buildingManagerId || !typeId) {
        toast.error('Please select Building Manager and Type');
        return;
      }
      
      const formData = {
        building_manager_id: buildingManagerId,
        type_id: typeId,
        inspections: inspections
      };
      
      const response = await inspectionsService.createInspections(formData);
      
      if (response && response.success) {
        toast.success(response.message || "Inspections have been added!");
        navigate('/inspections');
      } else {
        toast.error(response?.message || "Failed to create inspections");
      }
    } catch (error) {
      console.error('Error creating inspections:', error);
      toast.error("Failed to create inspections");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/inspections');
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumb Navigation */}
      <div className={styles.breadcrumbWrapper}>
        <ol className={styles.breadcrumb}>
          <li className={styles.breadcrumbItem}>
            <span onClick={() => navigate('/dashboard')} className={styles.breadcrumbLink} style={{ cursor: 'pointer', color: '#337ab7' }}>Dashboard</span>
          </li>
          <li className={styles.breadcrumbSeparator}>/</li>
          <li className={styles.breadcrumbItem}>
            <span onClick={() => navigate('/inspections')} className={styles.breadcrumbLink} style={{ cursor: 'pointer', color: '#337ab7' }}>Inspections</span>
          </li>
          <li className={styles.breadcrumbSeparator}>/</li>
          <li className={`${styles.breadcrumbItem} ${styles.breadcrumbActive}`}>Create Inspections</li>
        </ol>
      </div>

      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={`${styles.panel} ${styles.panelDefault} ${styles.panelForm}`}>
            <div className={styles.panelHeading}>
              <div className={styles.panelTitle}>
                <h3>Create Inspections</h3>
              </div>
            </div>
            <div className={styles.panelBody}>
              <form onSubmit={handleSubmit} className={styles.inspectionsForm}>
                <fieldset className={styles.formHorizontal}>
                  {/* Building Manager Section */}
                  <div className={styles.reportSectionHeader}>Building Manager</div>
                  
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.colSm5} ${styles.labelRequired}`}>
                      Building Manager
                    </label>
                    <div className={styles.colSm3}>
                      <select
                        id="building_manager_id"
                        className={styles.formControl}
                        required
                      >
                        <option value="">Please select</option>
                        {(buildingManagers || []).map(manager => (
                          <option key={manager.id} value={manager.id}>
                            {manager.name}
                          </option>
                        ))}
                      </select>
                      {errors.building_manager_id && (
                        <span className={styles.helpBlock}>{errors.building_manager_id}</span>
                      )}
                    </div>
                  </div>

                  {/* Type Section */}
                  <div className={styles.reportSectionHeader}>Type</div>
                  
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.colSm5} ${styles.labelRequired}`}>
                      Type
                    </label>
                    <div className={styles.colSm3}>
                      <select
                        id="type_id"
                        className={styles.formControl}
                        required
                      >
                        <option value="">Please select</option>
                        {(types || []).map(type => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                      {errors.type_id && (
                        <span className={styles.helpBlock}>{errors.type_id}</span>
                      )}
                    </div>
                  </div>

                  {/* Inspections Section */}
                  <div className={styles.reportSectionHeader}>Inspections</div>
                  
                  <div id="js-inspections-add-many"></div>

                  {/* Property Search */}
                  <div className={styles.row} style={{marginBottom: '15px'}}>
                    <div className={styles.colXs5}>
                      <div className={styles.propertySearchContainer} ref={propertyTypeaheadRef}>
                        <input
                          id="new_field_input"
                          className={`${styles.formControl} ${styles.typeahead} ${styles.jsPropertyFieldTypeahead}`}
                          type="text"
                          placeholder="Type property ID or name (or *)"
                          value={propertySearchValue}
                          onChange={(e) => handlePropertySearch(e.target.value)}
                          style={{border: '1px solid #0a7280'}}
                        />
                        {showPropertyTypeahead && (filteredProperties || []).length > 0 && (
                          <div className={styles.typeaheadDropdown}>
                            {(filteredProperties || []).map((property) => (
                              <div
                                key={property.id}
                                className={styles.typeaheadItem}
                                onClick={() => selectProperty(property)}
                              >
                                {property.name} - {property.address}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Inspections List */}
                  <div className={styles.controls}>
                    {(inspections || []).map((inspection, index) => (
                      <div key={index} className={styles.inspectionRow}>
                        <input
                          name={`inspections[${index}][property_id]`}
                          type="hidden"
                          value={inspection.property_id}
                        />
                        <input
                          name={`inspections[${index}][property_name]`}
                          type="hidden"
                          value={inspection.property_name}
                        />

                        {/* Delete Button */}
                        <div className={styles.inspectionDeleteCol}>
                          <button
                            className={styles.btnRemove}
                            type="button"
                            onClick={() => removeInspection(index)}
                          >
                            Delete
                          </button>
                        </div>

                        {/* Property Name */}
                        <div className={styles.inspectionPropertyCol}>
                          {inspection.property_name || 'Unknown Property'}
                        </div>

                        {/* Day Field */}
                        <div className={`${styles.inspectionFieldGroup} ${styles.dayField}`}>
                          <label>Day <span style={{color: '#d9534f'}}>*</span></label>
                          <input
                            required
                            name={`inspections[${index}][day]`}
                            value={(() => {
                              // Convert from dd/mm/yyyy to yyyy-mm-dd for HTML5 date input display
                              if (inspection.day && inspection.day.includes('/')) {
                                const parts = inspection.day.split('/');
                                if (parts.length === 3) {
                                  return `${parts[2]}-${parts[1]}-${parts[0]}`;
                                }
                              }
                              return inspection.day || '';
                            })()}
                            onChange={(e) => {
                              // Convert from yyyy-mm-dd to dd/mm/yyyy for storage
                              const val = e.target.value;
                              if (val) {
                                const [year, month, day] = val.split('-');
                                handleInspectionChange(index, 'day', `${day}/${month}/${year}`);
                              } else {
                                handleInspectionChange(index, 'day', '');
                              }
                            }}
                            type="date"
                          />
                          {errors[`inspections.${index}.day`] && (
                            <div className={styles.textDanger}>{errors[`inspections.${index}.day`]}</div>
                          )}
                        </div>

                        {/* From Time Field */}
                        <div className={`${styles.inspectionFieldGroup} ${styles.timeField}`}>
                          <label>From <span style={{color: '#d9534f'}}>*</span></label>
                          <input
                            required
                            name={`inspections[${index}][time_from]`}
                            value={inspection.time_from}
                            onChange={(e) => handleInspectionChange(index, 'time_from', e.target.value)}
                            type="time"
                          />
                          {errors[`inspections.${index}.time_from`] && (
                            <div className={styles.textDanger}>{errors[`inspections.${index}.time_from`]}</div>
                          )}
                        </div>

                        {/* To Time Field */}
                        <div className={`${styles.inspectionFieldGroup} ${styles.timeField}`}>
                          <label>To</label>
                          <input
                            name={`inspections[${index}][time_to]`}
                            value={inspection.time_to}
                            onChange={(e) => handleInspectionChange(index, 'time_to', e.target.value)}
                            type="time"
                          />
                          {errors[`inspections.${index}.time_to`] && (
                            <div className={styles.textDanger}>{errors[`inspections.${index}.time_to`]}</div>
                          )}
                        </div>

                        {/* Description Field */}
                        <div className={styles.inspectionDescriptionRow}>
                          <label>Description</label>
                          <textarea
                            name={`inspections[${index}][description]`}
                            value={inspection.description}
                            onChange={(e) => handleInspectionChange(index, 'description', e.target.value)}
                            placeholder="Enter description..."
                            rows="2"
                          />
                          {errors[`inspections.${index}.description`] && (
                            <div className={styles.textDanger}>{errors[`inspections.${index}.description`]}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                </fieldset>

                <fieldset className={styles.formHorizontal}>
                  <div className={`${styles.formGroup} ${styles.textRight}`}>
                    <div className={styles.colSm12}>
                      <button
                        type="submit"
                        className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard}`}
                        disabled={submitting}
                      >
                        {submitting ? 'Creating...' : 'Create'}
                      </button>
                    </div>
                  </div>
                </fieldset>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InspectionsCreate; 